import React, { ChangeEvent } from 'react';
import { Button, Card, CardContent, FormControl, Grid, Typography } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { AISummaryDialogProps } from './types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import styles from './AISummaryDialog.module.scss';

const AISummaryDialog = function ({
  onClose,
  handleAnalyzeFunction,
  documentType,
  setDocumentType,
  purpose,
  setPurpose,
  resultFormat,
  setResultFormat,
  detailLevel,
  setDetailLevel,
  simplifyJargon,
  setSimplifyJargon,
  contextualInformation,
  setContextualInformation,
  purposes,
}: AISummaryDialogProps) {
  const { t } = useTranslation();
  const icon = <CheckBoxOutlineBlankIcon fontSize='medium' />;
  const checkedIcon = <CheckBoxIcon fontSize='medium' />;

  return (
    <>
      {/* <Modal open={open} onClose={onClose} closeAfterTransition className='action-modal'>*/}
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent sx={{ padding: 0 }}>
              <Grid
                size={{ xs: 12 }}
                display={'flex'}
                className={styles.title_container}
                alignItems={'flex-start'}
                gap={'16px'}
              >
                {/*<AIIcon />
                <Typography variant='h6' gutterBottom>
                  {documentName}
                </Typography>*/}
              </Grid>
              <Grid display='flex' gap='16px' alignItems='flex-start'>
                <Typography>{t('messages.reviewSettings')}</Typography>
              </Grid>
              <Grid container spacing={2} marginTop={'10px'}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControl className='w-100'>
                    <InputLabel htmlFor='documentTypeSelect'>{t('labels.documentType')}</InputLabel>
                    <Select
                      className='w-100'
                      slotProps={{ input: { id: 'documentTypeSelect' } }}
                      onChange={(e: SelectChangeEvent<typeof documentType>) =>
                        setDocumentType(e.target.value)
                      }
                      name='documentType'
                      value={documentType}
                      tabIndex={-1}
                      //defaultOpen={true}
                    >
                      {' '}
                      <MenuItem value='other'>{t('documentTypes.other')}</MenuItem>
                      <MenuItem value='agreement'>{t('documentTypes.agreement')}</MenuItem>
                      <MenuItem value='contract'>{t('documentTypes.contract')}</MenuItem>
                      <MenuItem value='invoice'>{t('tagsLabels.documentType.invoice')}</MenuItem>
                      <MenuItem value='insurance'>
                        {t('tagsLabels.documentType.insurance')}
                      </MenuItem>
                      <MenuItem value='form'>{t('tagsLabels.documentType.form')}</MenuItem>
                      <MenuItem value='certificate or license'>
                        {t('documentTypes.certificate')}
                      </MenuItem>
                      <MenuItem value='policies and procedures'>
                        {t('tagsLabels.documentType.policiesAndProcedures')}
                      </MenuItem>
                      <MenuItem value='letter'>{t('tagsLabels.documentType.letter')}</MenuItem>
                      <MenuItem value='subscription'>{t('documentTypes.subscription')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControl className={styles.w100}>
                    <Autocomplete
                      className={styles.w100}
                      multiple
                      id='tags-standard'
                      openOnFocus={true}
                      options={purposes}
                      disableCloseOnSelect={true}
                      isOptionEqualToValue={(option, value) => option.title === value.title}
                      getOptionLabel={(option) => option.title}
                      value={purpose}
                      onChange={(
                        event: ChangeEvent<HTMLInputElement>,
                        newValue:
                          | {
                              title: string;
                              value: string;
                            }[]
                          | null
                      ) => {
                        setPurpose(newValue);
                      }}
                      renderOption={(props, option, { selected }) => {
                        const { ...optionProps } = props;
                        return (
                          <li key={option.value} {...optionProps}>
                            <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                            {option.title}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} variant='standard' label={t('labels.purpose')} />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl className='w-100'>
                    <InputLabel htmlFor='resultFormatSelect'>{t('labels.resultFormat')}</InputLabel>
                    <Select
                      className='w-100'
                      id='resultFormatSelect'
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setResultFormat(e.target.value);
                      }}
                      slotProps={{ input: { id: 'resultFormatSelect' } }}
                      name='resultFormat'
                      value={resultFormat}
                      tabIndex={-1}
                    >
                      <MenuItem value={''}>{''}</MenuItem>
                      <MenuItem value='table'>{t('resultFormat.table')}</MenuItem>
                      <MenuItem value='bullet point'>{t('resultFormat.bulletPoint')}</MenuItem>
                      <MenuItem value='narrative format'>
                        {t('resultFormat.narrativeFormat')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl className='w-100'>
                    <InputLabel htmlFor='detailLevelSelect'>{t('labels.detailLevel')}</InputLabel>
                    <Select
                      className='w-100'
                      id='detailLevelSelect'
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setDetailLevel(e.target.value);
                      }}
                      slotProps={{ input: { id: 'detailLevelSelect' } }}
                      name='detailLevel'
                      value={detailLevel}
                      tabIndex={-1}
                    >
                      <MenuItem value='high level'>{t('detailLevel.highLevel')}</MenuItem>
                      <MenuItem value='detailed'>{t('detailLevel.detailed')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  size={{ xs: 12, md: 12 }}
                  display={'flex'}
                  alignItems={'flex-start'}
                  className='checkbox-row'
                >
                  <Checkbox
                    style={{ marginLeft: -10, paddingTop: 0, paddingBottom: 0 }}
                    id='simplifyTechnicalOrLegalJargon'
                    checked={simplifyJargon}
                    onChange={(event) => setSimplifyJargon(event.target.checked)}
                  />
                  <label style={{ marginTop: 0 }} htmlFor='simplifyTechnicalOrLegalJargon'>
                    {t('labels.simplifyJargon')}
                  </label>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControl className={styles.textarea_container}>
                    <textarea
                      id='contextualInformation'
                      name='contextualInformation'
                      value={contextualInformation}
                      placeholder={t('messages.contextualInformationPlaceholder')}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        setContextualInformation(e.target.value);
                      }}
                    />
                    <InputLabel className={'label-transitioned'} htmlFor='contextualInformation'>
                      {t('labels.contextualInformation')}
                    </InputLabel>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
                <Button
                  variant='contained'
                  onClick={onClose}
                  className='btn-default btn-action-basic btn-cancel'
                >
                  {t('buttons.goBack')}
                </Button>
                <Button
                  variant='contained'
                  onClick={() => {
                    handleAnalyzeFunction();
                  }}
                  className='btn-default btn-action-basic'
                >
                  {t('buttons.analyze')}
                </Button>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/*</Modal>*/}
    </>
  );
};

export default AISummaryDialog;
